<template>
    <div id="app">
        <main>
            <!-- 导航栏 -->
            <nav class="nav shell">
                <a class="brand" href="#top" aria-label="Surfer 首页">
                    <img class="brand-logo" src="/surfer-logo.png" alt="Surfer" />
                    <span>Surfer</span>
                </a>
                <div class="nav-links">
                    <a :class="activeSection === 'top' ? 'active' : ''" href="#top">
                        <b>⌂</b>首页
                    </a>
                    <button @click="openDevices">
                        <b>▣</b>设备管理
                    </button>
                    <button @click="openRedeem">
                        <b>◇</b>兑换会员
                    </button>
                    <a :class="activeSection === 'member' ? 'active' : ''" href="#member">
                        <b>▤</b>商城
                    </a>
                    <a :class="`nav-hot ${activeSection === 'invite' ? 'active' : ''}`" href="#invite">
                        <b>♢</b>邀请返佣 <i>HOT</i>
                    </a>
                </div>
                <div class="nav-tools">
                    <button
                        v-if="!isLoggedIn"
                        class="nav-login"
                        @click="requireLogin('管理会员与设备')"
                    >
                        注册 / 登录
                    </button>
                    <div v-else class="user-menu" @click="userMenuOpen = !userMenuOpen">
                        <span class="user-email">{{ loginEmail }}</span>
                        <b class="caret">▾</b>
                        <div v-if="userMenuOpen" class="user-dropdown" @click.stop>
                            <button @click="logout">退出登录</button>
                        </div>
                    </div>
                </div>
            </nav>

            <!-- 英雄区 -->
            <section class="hero shell" id="top">
                <div class="hero-copy">
                    <div class="eyebrow">
                        <span /> 全新 Surfer 3.0
                    </div>
                    <h1>
                        连接世界，
                        <br />
                        <em>快得更自然。</em>
                    </h1>
                    <p class="lead">
                        为每一台设备打造的轻盈网络体验。智能选择更快线路，稳定、安全，始终在线。
                    </p>
                    <div class="hero-actions">
                        <button class="primary" @click="downloadApp(device)">
                            获取 {{ device }} 客户端 <b>↓</b>
                        </button>
                        <button
                            v-if="device === 'iOS'"
                            class="backup-hero"
                            @click="panel = 'ios-tutorial'"
                        >
                            安装教程
                        </button>
                        <button
                            v-else-if="backupUrls[device] && backupUrls[device].length > 0"
                            class="backup-hero"
                            @click="showBackupOptions(device)"
                        >
                            备用下载
                        </button>
                    </div>
                    <p class="device-hint">
                        <i /> 已识别此设备：{{ device }}
                    </p>
                </div>
                <div class="product-stage">
                    <div class="glow glow-one"></div>
                    <div class="glow glow-two"></div>
                    <div class="window-card">
                        <img src="/surfer-app.png" alt="Surfer UI" decoding="async" fetchpriority="high" @load="heroImgLoaded = true" :class="{ 'img-loaded': heroImgLoaded }" />
                    </div>
                    <div class="speed-chip">
                        <small>智能线路</small>
                        <strong>32<sup>ms</sup></strong>
                        <span>✓ 连接稳定</span>
                    </div>
                </div>
            </section>

            <!-- 信任条带 -->
            <div class="trust-strip">
                <span>3000+ 全球节点</span>
                <i></i>
                <span>100% 隐私保护</span>
                <i></i>
                <span>7*24 技术支持</span>
                <i></i>
                <span>秒级智能连接</span>
            </div>

            <!-- 下载区域 -->
            <section class="section shell" id="download">
                <div class="section-header">
                    <h2>多平台支持</h2>
                    <p>为每一台设备打造轻盈体验</p>
                </div>

                <div class="platforms">
                    <div
                        v-for="platform in platforms"
                        :key="platform.name"
                        class="platform"
                    >
                        <span class="platform-icon">{{ platform.icon }}</span>
                        <h3>{{ platform.name }}</h3>
                        <p>{{ platform.note }}</p>
                        <div class="platform-action">
                            <button @click="downloadApp(platform.name)" class="download-btn">
                                {{ platform.action }}
                            </button>
                            <button @click="showBackupOptions(platform.name)" class="download-btn secondary" v-if="backupUrls[platform.name] && backupUrls[platform.name].length > 0">
                                备用下载
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 会员商城 -->
            <section v-if="isLoggedIn" class="section shell" id="member">
                <div class="section-header">
                    <h2>灵活的会员套餐</h2>
                    <p>选择适合你的方案，享受极速稳定的网络体验</p>
                </div>

                <div v-if="plansLoading" style="text-align:center; padding:40px 0; color:#999;">正在加载套餐...</div>

                <div v-else>
                    <div v-for="g in groups" :key="g.name" class="plan-group">
                        <div class="plan-group-title">{{ groupLabel(g.name) }}</div>
                        <div class="plans-grid">
                            <div
                                v-for="p in g.list"
                                :key="g.name + '_' + p.plan_id + '_' + p.period"
                                class="plan-card"
                                :class="{ recommended: isSelected(g, p) }"
                                @click="selectPlan(g, p)"
                            >
                                <h3>{{ p.title }}</h3>
                                <div class="price">
                                    <span class="symbol">¥</span><span class="amount">{{ yuan(p.price) }}</span>
                                </div>
                                <div class="period">{{ p.period_name }}</div>
                                <ul class="features">
                                    <li>赠送 {{ p.give_time || 0 }} 天</li>
                                    <li>{{ p.device_quantity }} 台设备</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div v-if="!groups.length && !plansLoading" style="text-align:center; color:#999; padding:20px;">
                        {{ shopMessage || '暂无可用套餐' }}
                    </div>

                    <!-- 优惠券输入区域 -->
                    <div v-if="groups.length" class="coupon-section">
                        <input
                            v-model="couponCode"
                            type="text"
                            placeholder="请输入优惠码（可选）"
                            class="coupon-input"
                            maxlength="50"
                        />
                    </div>

                    <button
                        v-if="groups.length"
                        class="checkout"
                        :disabled="!selected || paying"
                        @click="submitPayment"
                    >
                        {{ paying ? '下单中...' : (selected ? '立即购买 · ¥' + yuan(selected.item.price) : '请选择套餐') }}
                    </button>
                </div>
            </section>

            <!-- 未登录时提示 -->
            <section v-else class="section shell" id="member">
                <div class="section-header">
                    <h2>灵活的会员套餐</h2>
                    <p>选择适合你的方案，享受极速稳定的网络体验</p>
                </div>
                <div style="text-align: center; padding: 60px 0;">
                    <p style="font-size: 18px; color: #666; margin-bottom: 30px;">请先登录查看会员套餐</p>
                    <button @click="requireLogin('购买会员')" class="checkout">立即登录</button>
                </div>
            </section>

            <!-- 邀请返佣 -->
            <section class="invite-section" id="invite">
                <div class="invite-inner shell">
                    <div class="invite-left">
                        <div class="invite-badge">限时邀请计划 · 持续返佣</div>
                        <h2 class="invite-title">分享一次，<br><em>持续获得收益。</em></h2>
                        <p class="invite-desc">邀请好友加入 Surfer。好友每次续费，你都能持续获得返佣；团队越大，长期收益越高。</p>
                        <button class="invite-btn" @click="shareInvite">立即分享邀请链接 <b>↗</b></button>
                        <p class="invite-hint">返佣满 ¥300 即可申请提现</p>
                    </div>
                    <div class="invite-right">
                        <div class="invite-card">
                            <div class="invite-card-head">
                                <span class="invite-card-title">三级持续返佣</span>
                                <span class="invite-card-tag">收益数据示例</span>
                            </div>
                            <div class="invite-levels">
                                <div class="level-box">
                                    <span class="level-name">一级</span>
                                    <strong class="level-pct">20%</strong>
                                    <span class="level-sub">直接邀请</span>
                                </div>
                                <div class="level-box">
                                    <span class="level-name">二级</span>
                                    <strong class="level-pct">10%</strong>
                                    <span class="level-sub">好友邀请</span>
                                </div>
                                <div class="level-box">
                                    <span class="level-name">三级</span>
                                    <strong class="level-pct">5%</strong>
                                    <span class="level-sub">团队邀请</span>
                                </div>
                            </div>
                            <div class="invite-stats">
                                <div class="stat-item">
                                    <span class="stat-label">累计成功推荐</span>
                                    <strong class="stat-value">917<small> 人</small></strong>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-label">持续产生收益</span>
                                    <strong class="stat-value">¥ 8,426<small>.00</small></strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 底部 -->
            <footer class="footer shell">
                <div class="support">
                    <h3>需要帮助？</h3>
                    <div class="support-actions">
                        <button @click="openLink('https://t.me/quyutu')">Telegram</button>
                    </div>
                </div>
                <p>&copy; 2024 Surfer. All rights reserved.</p>
            </footer>
        </main>

        <!-- 登录/注册模态框 -->
        <div v-if="panel === 'login'" class="modal-overlay" @click.self="closePanel">
            <div class="modal">
                <button class="modal-close" @click="closePanel">✕</button>
                <div v-if="authMode === 'login'" class="modal-content">
                    <div class="modal-icon">L</div>
                    <h2 class="modal-title">{{ loginReason }}</h2>
                    <input
                        v-model="loginForm.email"
                        type="email"
                        placeholder="邮箱地址"
                        class="modal-input"
                    />
                    <input
                        v-model="loginForm.password"
                        type="password"
                        placeholder="密码"
                        class="modal-input"
                    />
                    <button
                        class="modal-primary"
                        @click="submitLogin"
                    >
                        登录
                    </button>
                    <p class="modal-footer">
                        还没有账户？
                        <button @click="authMode = 'register'" class="link-btn">立即注册</button>
                    </p>
                    <p class="modal-footer">
                        <button @click="authMode = 'reset'" class="link-btn">忘记密码？</button>
                    </p>
                </div>

                <div v-else-if="authMode === 'register'" class="modal-content">
                    <div class="modal-icon">R</div>
                    <h2 class="modal-title">创建账户</h2>
                    <input
                        v-model="loginForm.email"
                        type="email"
                        placeholder="邮箱地址"
                        class="modal-input"
                    />
                    <input
                        v-model="loginForm.password"
                        type="password"
                        placeholder="设置密码"
                        class="modal-input"
                    />
                    <input
                        v-model="loginForm.confirmPassword"
                        type="password"
                        placeholder="确认密码"
                        class="modal-input"
                    />
                    <button
                        class="modal-primary"
                        @click="submitRegister"
                    >
                        立即注册
                    </button>
                    <p class="modal-footer">
                        已有账户？
                        <button @click="authMode = 'login'" class="link-btn">直接登录</button>
                    </p>
                </div>

                <div v-else-if="authMode === 'reset'" class="modal-content">
                    <div class="modal-icon">P</div>
                    <h2 class="modal-title">重置密码</h2>
                    <input
                        v-model="resetForm.email"
                        type="email"
                        placeholder="邮箱地址"
                        class="modal-input"
                    />
                    <div class="code-group">
                        <input
                            v-model="resetForm.code"
                            type="text"
                            placeholder="验证码"
                            class="modal-input code-input"
                        />
                        <button @click="sendVerificationCode" class="code-btn">发送验证码</button>
                    </div>
                    <input
                        v-model="resetForm.newPassword"
                        type="password"
                        placeholder="新密码"
                        class="modal-input"
                    />
                    <button class="modal-primary" @click="resetPassword">
                        重置密码
                    </button>
                    <p class="modal-footer">
                        <button @click="authMode = 'login'" class="link-btn">返回登录</button>
                    </p>
                </div>
            </div>
        </div>

        <!-- 兑换模态框 -->
        <div v-if="panel === 'redeem'" class="modal-overlay" @click.self="closePanel">
            <div class="modal">
                <button class="modal-close" @click="closePanel">✕</button>
                <div class="modal-content">
                    <div class="modal-icon">C</div>
                    <h2 class="modal-title">兑换会员权益</h2>
                    <input
                        v-model="redeemCode"
                        type="text"
                        placeholder="请输入兑换码"
                        class="modal-input"
                    />
                    <button class="modal-primary" @click="submitRedeem">
                        立即兑换
                    </button>
                </div>
            </div>
        </div>

        <!-- 备用下载选择弹窗 -->
        <div v-if="panel === 'backup-options'" class="modal-overlay" @click.self="closePanel">
            <div class="modal">
                <button class="modal-close" @click="closePanel">✕</button>
                <div class="modal-content">
                    <div class="modal-icon">D</div>
                    <h2 class="modal-title">{{ currentPlatform }} - 选择下载源</h2>
                    <div class="backup-list">
                        <button 
                            v-for="(backup, index) in currentBackups" 
                            :key="index"
                            @click="downloadBackup(backup.url, backup.name)"
                            class="backup-option"
                        >
                            {{ backup.name }}
                            <span class="file-size" v-if="backup.size">{{ backup.size }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 设备管理弹窗 -->
        <div v-if="panel === 'devices'" class="modal-overlay" @click.self="closePanel">
            <div class="modal">
                <button class="modal-close" @click="closePanel">✕</button>
                <div class="modal-content">
                    <div class="modal-icon">E</div>
                    <h2 class="modal-title">设备管理</h2>
                    <div v-if="devicesLoading" style="text-align:center; color:#999; padding:20px;">正在加载...</div>
                    <div v-else>
                        <div v-if="!devices.length" style="text-align:center; color:#999; padding:20px;">暂无登录设备</div>
                        <div v-else class="device-list">
                            <div v-for="(item, index) in devices" :key="index" class="device-row">
                                <span class="device-type">{{ deviceTypeName(item.type) }}</span>
                                <button class="device-unbind" @click="unbindDevice(item)">解绑</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- iOS 安装教程弹窗 -->
        <div v-if="panel === 'ios-tutorial'" class="modal-overlay" @click.self="closePanel">
            <div class="modal modal-tutorial">
                <button class="modal-close" @click="closePanel">✕</button>
                <h2 class="modal-title">iOS 下载教程</h2>
                <div class="tut-search">App Store 搜索：<strong>Surfer 加速器</strong></div>

                <div class="tut-block">
                    <h3>准备：先退出当前 Apple ID</h3>
                    <p style="color:#555;font-size:14px;margin-bottom:8px;">切换美区账号前，需要先退出原来的 Apple ID：</p>
                    <ol>
                        <li>打开 <strong>设置</strong> → 点击顶部的 <strong>Apple 账户</strong>（你的头像/名字）。</li>
                        <li>下滑找到并进入 <strong>媒体与购买项目</strong>。</li>
                        <li>点击 <strong>退出登录</strong>。</li>
                        <li>退出后再登录美区 Apple ID 即可。</li>
                    </ol>
                    <div class="tut-info">💡 只需退出"媒体与购买项目"里的账号即可，不用退出 iCloud，照片、通讯录等数据不受影响。</div>
                </div>

                <div class="tut-block">
                    <h3>方法一：共享 Apple ID（快速尝鲜）</h3>
                    <div class="tut-info">
                        <p><strong>账号信息：</strong></p>
                        <div class="cred-row">
                            <span class="cred-text">邮箱：{{ sharedAppleId.email }}</span>
                            <button class="cred-copy" @click="copyText(sharedAppleId.email, '账号')">复制</button>
                        </div>
                        <div class="cred-row">
                            <span class="cred-text">密码：{{ sharedAppleId.password }}</span>
                            <button class="cred-copy" @click="copyText(sharedAppleId.password, '密码')">复制</button>
                        </div>
                    </div>

                    <!-- 双重认证醒目提示 -->
                    <div class="tut-alert">
                        <div class="tut-alert-title">🚨 登录前必看！</div>
                        <p>登录时如果弹出 <strong>「双重认证 / 提升账户安全性」</strong> 窗口：</p>
                        <div class="tut-alert-steps">
                            <span>点击「其他选项」</span>
                            <b>→</b>
                            <span>选择「不升级」</span>
                        </div>
                        <p class="tut-alert-no">❌ 千万不要点「继续」，不要绑定你的手机号！</p>
                        <p class="tut-alert-sub">一旦绑定手机号，账号会被锁定，所有人都将无法使用。</p>
                    </div>

                    <ol>
                        <li>打开 App Store，切换到该账号。</li>
                        <li>如弹出双重认证提示，选择 <strong>其他选项 → 不升级</strong>。</li>
                        <li>搜索：<strong>Surfer 加速器</strong>下载安装。</li>
                    </ol>
                    <div class="tut-warn">⚠️ 重要警告：共享 Apple ID 仅适合"短期尝鲜"，且需承担隐私泄露、手机被锁、账号失效等风险，不建议长期使用。自主注册或购买独享 ID 才是最优解，账号归属自己更安全。</div>
                </div>

                <div class="tut-block">
                    <h3>方法二：购买现成美区 Apple ID（最省事）</h3>
                    <ol>
                        <li>打开购买页面：<a href="https://surferv.org/" target="_blank">https://surferv.org/</a></li>
                        <li>按提示购买一个美区 Apple ID。</li>
                        <li>收到账号后，打开 App Store，切换到该账号。</li>
                        <li>搜索：<strong>Surfer 加速器</strong>下载安装。</li>
                    </ol>
                    <div class="tut-important">
                        <strong>⭐ 重要：购买 ID 后请务必操作</strong>
                        <p>登录账号后，第一时间修改密码、绑定你自己的手机号，并解绑原手机号，方便后续长期登录使用，避免账号被找回。</p>
                    </div>
                    <div class="tut-warn">注意：只登录 App Store 商店即可，不要登录 iCloud。</div>
                </div>

                <button class="modal-primary" @click="closePanel">关闭</button>
            </div>
        </div>

        <!-- Mac 安装教程弹窗 -->
        <div v-if="panel === 'mac-tutorial'" class="modal-overlay" @click.self="closePanel">
            <div class="modal modal-tutorial">
                <button class="modal-close" @click="closePanel">✕</button>
                <h2 class="modal-title">Mac 下载 / 安装教程</h2>

                <div class="tut-block">
                    <h3>1. 下载对应芯片版本</h3>
                    <div class="mac-chip-btns">
                        <button class="download-btn" @click="downloadMacChip('M')">M 系列芯片</button>
                        <button class="download-btn" @click="downloadMacChip('Intel')">Intel 芯片</button>
                    </div>
                    <p style="color:#999;font-size:13px;margin-top:8px;">不确定芯片？点开左上角  → 关于本机 查看。</p>
                </div>

                <div class="tut-block">
                    <h3>2. 解决"已损坏，无法打开"提示</h3>
                    <ol>
                        <li>打开 <strong>终端（Terminal）</strong>。</li>
                        <li>执行命令：
                            <div class="mac-cmd-box">
                                <pre>sudo xattr -cr /Applications/Surfer加速器.app</pre>
                                <button class="mac-copy-btn" @click="copyMacCommand">一键复制</button>
                            </div>
                        </li>
                        <li>输入 Mac 登录密码（不显示星号），回车即可。</li>
                        <li>执行后可正常双击打开 <strong>Surfer加速器</strong>。</li>
                    </ol>
                </div>

                <div class="tut-block">
                    <h3>3. 连接节点时需要管理员权限</h3>
                    <ol>
                        <li>首次连接节点时会弹出输入管理员权限的提示框。</li>
                        <li>这是 macOS 系统安全机制，属于正常行为。</li>
                        <li>输入 Mac 登录密码即可授予权限。</li>
                        <li>授权后应用可以正常建立连接。</li>
                    </ol>
                    <div class="tut-info">💡 提示：后续使用无需重复输入密码，仅首次需要授权。</div>
                </div>

                <button class="modal-primary" @click="closePanel">关闭</button>
            </div>
        </div>

        <!-- 自定义客服按钮 -->
        <button class="my-kefu" @click="openKefu">在线<br>客服</button>

        <!-- 提示消息 -->
        <div v-if="toast" class="toast">{{ toast }}</div>
    </div>
</template>

<script>
import { getLogin, getRegister, redeemCodeWebsite, planFetchWeb, payWebsite, getListWebsite, getOutBindWebsite } from './api/api.js';

export default {
    name: 'App',
    data() {
        return {
            device: 'Windows',
            panel: null,
            authMode: 'login',
            loginReason: '管理会员与设备',
            toast: '',
            activeSection: 'top',
            redeemCode: '',
            couponCode: '',
            currentBackups: [],
            currentPlatform: '',
            devices: [],
            devicesLoading: false,
            loginEmail: '',
            inviteUrl: '',
            heroImgLoaded: false,
            userMenuOpen: false,
            groups: [],
            selected: null,
            plansLoading: false,
            paying: false,
            shopMessage: '',
            // 共享 Apple ID（改密码只需改这里）
            sharedAppleId: {
                email: 'BoehnleinWiacek42@outlook.com',
                password: 'Aa002211'
            },
            loginForm: {
                email: '',
                code: '',
                password: '',
                confirmPassword: ''
            },
            resetForm: {
                email: '',
                code: '',
                newPassword: ''
            },
            downloadUrls: {
                'Windows': {
                    url: 'https://share.weiyun.com/lzEcY6lU',
                    name: 'surfer2.0.1pc.zip',
                    size: '14.8M'
                },
                'macOS': {
                    url: 'https://share.weiyun.com/Ujp7IP9E',
                    name: 'Surfer-MacOS_2.0.1_m.dmg',
                    size: '29.2M'
                },
                'iOS': {
                    url: 'https://apps.apple.com/us/app/surfer-%E5%8A%A0%E9%80%9F%E5%99%A8/id6759246083?l=zh-Hans-CN',
                    name: 'App Store',
                    size: ''
                },
                'Android': {
                    url: 'https://share.weiyun.com/kkgXxTyf',
                    name: 'surfer-Android.apk',
                    size: '35.4M'
                }
            },
            backupUrls: {
                'Windows': [
                    { url: 'http://wwbcd.zizi2.com/surferpc', name: '备用下载1', size: '' },
                    { url: 'https://1drv.ms/u/c/08770FA9D63F301D/IQBimpqEyNxfTo52hfq7xeVOAeok4J444qmPrZ5RhgPidJE?e=A3IbBp', name: 'OneDrive 备用', size: '' }
                ],
                'macOS': [
                    { url: 'http://wwbcd.zizi2.com/qiyutuziMM', name: '备用下载1（M系列芯片）', size: '' },
                    { url: 'http://wwbcd.zizi2.com/qiyutuziIntel', name: '备用下载2（Intel芯片）', size: '' },
                    { url: 'https://1drv.ms/u/c/08770FA9D63F301D/IQD1MB547S4bSpkPO-a20za1AXkSgR4F3KmnEPW2Ad-SIYk?e=pYrL62', name: 'OneDrive 备用（M系列芯片）', size: '' },
                    { url: 'https://1drv.ms/u/c/08770FA9D63F301D/IQD1MB547S4bSpkPO-a20za1AXkSgR4F3KmnEPW2Ad-SIYk?e=pYrL62', name: 'OneDrive 备用（Intel芯片）', size: '' }
                ],
                'iOS': [],
                'Android': [
                    { url: 'http://wwbcd.zizi2.com/surferapp', name: '备用下载1', size: '' },
                    { url: 'https://1drv.ms/u/c/08770FA9D63F301D/IQDLAn8M9hn8T756YftbWgIeAbKCQTT3Tv2WMQAa_iCkWHY?e=ozrkw3', name: 'OneDrive 备用', size: '' }
                ]
            },
            platforms: [
                { name: 'Windows', icon: '⊞', note: 'Windows 10 及以上', action: '点击下载' },
                { name: 'macOS', icon: '●', note: '通用版本', action: '点击下载' },
                { name: 'iOS', icon: '◉', note: 'iPhone 与 iPad', action: '前往安装' },
                { name: 'Android', icon: '◆', note: 'Android 8.0 及以上', action: '下载 APK' }
            ]
        };
    },
    computed: {
        isLoggedIn() {
            return !!this.loginEmail;
        }
    },
    mounted() {
        const ua = navigator.userAgent.toLowerCase();
        if (/iphone|ipad|ipod/.test(ua)) this.device = 'iOS';
        else if (/android/.test(ua)) this.device = 'Android';
        else if (/mac/.test(ua)) this.device = 'macOS';
        else this.device = 'Windows';

        // 从缓存恢复登录状态
        this.loginEmail = localStorage.getItem('loginEmail') || '';
        this.inviteUrl = localStorage.getItem('invite_url') || '';

        window.addEventListener('scroll', this.handleScroll);
        this.handleScroll();

        // 已登录才拉套餐（未登录不请求，避免"未登录或已过期"提示）
        if (this.isLoggedIn) {
            this.loadPlans();
        }
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        handleScroll() {
            const sections = ['top', 'download', 'member', 'invite'];
            for (const id of sections) {
                const element = document.getElementById(id);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top < window.innerHeight / 2 && rect.bottom > window.innerHeight / 2) {
                        this.activeSection = id;
                        break;
                    }
                }
            }
        },
        notify(message) {
            this.toast = message;
            window.setTimeout(() => { this.toast = ''; }, 2600);
        },
        requireLogin(reason) {
            this.loginReason = reason;
            this.authMode = 'login';
            this.panel = 'login';
        },
        openDevices() {
            if (!this.isLoggedIn) {
                this.requireLogin('管理已登录设备');
                return;
            }
            this.panel = 'devices';
            this.loadDevices();
        },
        openRedeem() {
            if (!this.isLoggedIn) {
                this.requireLogin('兑换会员权益');
                return;
            }
            this.panel = 'redeem';
        },
        deviceTypeName(type) {
            const map = { 1: '安卓手机', 2: '苹果手机', 3: 'Mac', 4: 'Windows' };
            return map[type] || '未知设备';
        },
        async loadDevices() {
            if (!this.isLoggedIn) return;
            this.devicesLoading = true;
            try {
                const res = await getListWebsite();
                if (res.data.code === 200 || Array.isArray(res.data.data)) {
                    this.devices = res.data.data || [];
                } else {
                    this.devices = [];
                    this.notify(res.data.message);
                }
            } catch (error) {
                this.devices = [];
                this.notify(error.message || '获取设备列表失败');
            } finally {
                this.devicesLoading = false;
            }
        },
        async unbindDevice(item) {
            try {
                const res = await getOutBindWebsite({
                    equipment_id: item.equipment_id,
                    type: item.type
                });
                if (res.data.code === 200) {
                    this.notify(res.data.message || '解绑成功');
                    this.loadDevices();
                } else {
                    this.notify(res.data.message || '解绑失败');
                }
            } catch (error) {
                this.notify(error.message || '解绑失败');
            }
        },
        closePanel() {
            this.panel = null;
        },
        downloadApp(platform) {
            // iOS 弹安装教程，不直接下载
            if (platform === 'iOS') {
                this.panel = 'ios-tutorial';
                return;
            }
            // macOS 弹芯片选择+教程
            if (platform === 'macOS') {
                this.panel = 'mac-tutorial';
                return;
            }
            const urlObj = this.downloadUrls[platform];
            if (!urlObj) return;
            const a = document.createElement('a');
            a.href = urlObj.url;
            a.download = urlObj.name;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        downloadMacChip(chip) {
            const url = chip === 'M' ? this.downloadUrls['macOS'].url : 'http://wwbcd.zizi2.com/qiyutuziIntel';
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        copyMacCommand() {
            const command = 'sudo xattr -cr /Applications/Surfer加速器.app';
            navigator.clipboard.writeText(command).then(() => {
                this.notify('命令已复制，可直接粘贴到终端执行');
            }).catch(() => {
                this.notify('复制失败，请手动复制');
            });
        },
        // 通用复制（带兜底，兼容 http 页面和 iOS）
        copyText(text, label) {
            const done = () => this.notify(`${label}已复制`);
            const fallback = () => {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.setAttribute('readonly', '');
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                ta.setSelectionRange(0, text.length); // iOS 需要
                try {
                    document.execCommand('copy') ? done() : this.notify('复制失败，请长按手动复制');
                } catch (e) {
                    this.notify('复制失败，请长按手动复制');
                }
                document.body.removeChild(ta);
            };
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(done).catch(fallback);
            } else {
                fallback();
            }
        },
        showBackupOptions(platform) {
            this.currentPlatform = platform;
            this.currentBackups = this.backupUrls[platform] || [];
            this.panel = 'backup-options';
        },
        downloadBackup(url, name) {
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            this.closePanel();
        },
        async submitLogin() {
            if (!this.loginForm.email || !this.loginForm.password) {
                this.notify('请输入邮箱和密码');
                return;
            }
            try {
                const res = await getLogin({
                    email: this.loginForm.email,
                    password: this.loginForm.password
                });
                // 登录接口：成功 code == 200，token 在 data.data.data
                if (res.data.code === 200) {
                    localStorage.setItem('token', res.data.data.data.token);
                    localStorage.setItem('auth_data', res.data.data.data.auth_data);
                    localStorage.setItem('loginEmail', this.loginForm.email);
                    // 邀请链接（后端返回在 data.data.data.invite_url，与 token 同层）
                    const inviteUrl = res.data.data.data.invite_url || '';
                    if (inviteUrl) {
                        localStorage.setItem('invite_url', inviteUrl);
                        this.inviteUrl = inviteUrl;
                    }
                    this.loginEmail = this.loginForm.email;
                    this.notify('登录成功');
                    this.loginForm.email = '';
                    this.loginForm.password = '';
                    this.closePanel();
                    this.loadPlans();
                } else {
                    this.notify(res.data.message);
                }
            } catch (error) {
                this.notify(error.message || '登录出错');
            }
        },
        async submitRegister() {
            if (!this.loginForm.email || !this.loginForm.password || !this.loginForm.confirmPassword) {
                this.notify('请填写完整信息');
                return;
            }
            if (this.loginForm.password !== this.loginForm.confirmPassword) {
                this.notify('两次密码不一致');
                return;
            }
            try {
                const res = await getRegister({
                    email: this.loginForm.email,
                    password: this.loginForm.password
                });
                // 注册接口：成功 code == 1，注册成功不算登录，跳转登录页
                if (res.data.code === 1) {
                    this.notify('注册成功，请登录');
                    // 保留邮箱方便直接登录，清空密码
                    this.loginForm.password = '';
                    this.loginForm.confirmPassword = '';
                    this.authMode = 'login';
                } else {
                    this.notify(res.data.message);
                }
            } catch (error) {
                this.notify(error.message || '注册出错');
            }
        },
        async resetPassword() {
            if (!this.resetForm.email || !this.resetForm.newPassword) {
                this.notify('请填写邮箱和新密码');
                return;
            }
            try {
                const res = await fetch('/api/resetPasswordWebsite', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') || '' },
                    body: JSON.stringify(this.resetForm)
                }).then(r => r.json());
                if (res.code === 200 || res.code === 1) {
                    this.notify(res.message || '密码重置成功');
                    this.panel = 'login';
                    this.authMode = 'login';
                    this.resetForm = { email: '', code: '', newPassword: '' };
                } else {
                    this.notify(res.message);
                }
            } catch (error) {
                this.notify(error.message || '重置失败');
            }
        },
        async submitRedeem() {
            if (!this.redeemCode) {
                this.notify('请输入兑换码');
                return;
            }
            try {
                const res = await redeemCodeWebsite({ code: this.redeemCode });
                // 兑换接口：成功 code == 200
                if (res.data.code === 200) {
                    this.notify(res.data.message || '兑换成功');
                    this.redeemCode = '';
                    this.closePanel();
                } else {
                    this.notify(res.data.message);
                }
            } catch (error) {
                this.notify(error.message || '兑换出错');
            }
        },
        // 分 -> 元
        yuan(v) {
            const n = Number(v || 0);
            if (!isFinite(n)) return v;
            return (n / 100).toFixed(2);
        },
        groupLabel(name) {
            if (name === 'svip会员') return 'SVIP 会员';
            if (name === 'vip会员') return 'VIP 会员';
            return name;
        },
        isSelected(g, p) {
            if (!this.selected) return false;
            return this.selected.groupName === g.name &&
                this.selected.item.plan_id === p.plan_id &&
                this.selected.item.period === p.period;
        },
        selectPlan(g, p) {
            this.selected = { groupName: g.name, item: p };
        },

        async submitPayment() {
            if (!this.isLoggedIn) {
                this.requireLogin('购买会员');
                return;
            }
            if (!this.selected) {
                this.notify('请选择一个套餐');
                return;
            }
            if (this.paying) return;
            this.paying = true;
            try {
                const p = this.selected.item;
                const res = await payWebsite({
                    period: p.period,
                    plan_id: p.plan_id,
                    gift_time: p.give_time || 0,
                    coupon_code: this.couponCode || '' // 添加优惠券参数
                });
                // 下单接口：成功 code == 1，支付链接在 data.data.pay_url
                if (res.data.code === 1 && res.data.data && res.data.data.pay_url) {
                    window.location.href = res.data.data.pay_url;
                } else {
                    this.notify(res.data.message || '下单失败');
                }
            } catch (error) {
                this.notify(error.message || '下单失败，请重试');
            } finally {
                this.paying = false;
            }
        },
        async loadPlans() {
            if (!this.isLoggedIn) return;
            this.plansLoading = true;
            this.shopMessage = '';
            try {
                const res = await planFetchWeb({});
                // 商城接口：成功 code == 1，data 是分组数组
                if (res.data.code === 1 && Array.isArray(res.data.data)) {
                    this.groups = res.data.data
                        .filter(x => x && Array.isArray(x.list))
                        .map(x => ({
                            name: x.name,
                            list: x.list.filter(i => i && i.plan_id != null && i.period && i.price != null)
                        }))
                        .filter(x => x.list.length > 0);
                    if (!this.groups.length) this.shopMessage = '暂无可用套餐';
                    // 默认选中第一个
                    if (this.groups.length && this.groups[0].list.length) {
                        this.selectPlan(this.groups[0], this.groups[0].list[0]);
                    }
                } else {
                    this.groups = [];
                    this.shopMessage = res.data.message || '获取套餐失败';
                }
            } catch (error) {
                this.groups = [];
                this.shopMessage = '获取套餐失败，请重试';
            } finally {
                this.plansLoading = false;
            }
        },
        logout() {
            localStorage.removeItem('token');
            localStorage.removeItem('auth_data');
            localStorage.removeItem('loginEmail');
            localStorage.removeItem('invite_url');
            this.loginEmail = '';
            this.inviteUrl = '';
            this.userMenuOpen = false;
            this.groups = [];
            this.selected = null;
            this.couponCode = '';
            this.notify('已退出登录');
        },
        sendVerificationCode() {
            this.notify('验证码已发送到邮箱');
        },
        openLink(url) {
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        openKefu() {
            if (window.$crisp) {
                window.$crisp.push(['do', 'chat:show']);
                window.$crisp.push(['do', 'chat:open']);
            } else {
                this.notify('客服加载中，请稍候');
            }
        },
        shareInvite() {
            if (!this.isLoggedIn) {
                this.requireLogin('获取专属邀请链接');
                return;
            }
            const link = this.inviteUrl || localStorage.getItem('invite_url') || '';
            if (!link) {
                this.notify('未获取到邀请链接，请重新登录');
                return;
            }
            if (navigator.clipboard) {
                navigator.clipboard.writeText(link).then(() => {
                    this.notify('邀请链接已复制，快去分享吧！');
                }).catch(() => {
                    this.notify('邀请链接：' + link);
                });
            } else {
                this.notify('邀请链接：' + link);
            }
        }
    }
};
</script>

<style scoped>
@import './global.css';

.brand-logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border-radius: 8px;
    display: block;
}
.window-card img { opacity: 0; transition: opacity 0.5s ease; }
.window-card img.img-loaded { opacity: 1; }
.modal-tutorial { max-width: 480px; max-height: 82vh; overflow-y: auto; text-align: left; }
.tut-search { padding: 12px; border-radius: 8px; background: #f0f9ff; border: 1px solid #a8d8ff; color: #0051a8; font-size: 14px; margin-bottom: 16px; text-align: center; }
.tut-block { margin-bottom: 20px; }
.tut-block h3 { font-size: 15px; color: #333; margin: 0 0 10px 0; }
.tut-block ol { margin: 0; padding-left: 20px; color: #555; font-size: 14px; line-height: 1.8; }
.tut-block p { margin: 4px 0; color: #555; font-size: 14px; }
.tut-block a { color: #667eea; word-break: break-all; }
.tut-info { padding: 12px; border-radius: 8px; background: #f0f9ff; border: 1px solid #a8d8ff; color: #0051a8; font-size: 13px; line-height: 1.7; margin: 10px 0; }
.tut-info p { margin: 4px 0; color: #0051a8; }
.cred-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 6px 0; }
.cred-text { word-break: break-all; color: #0051a8; }
.cred-copy { flex-shrink: 0; padding: 4px 12px; background: #667eea; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; }
.cred-copy:hover { background: #5568d3; }
.tut-warn { margin-top: 12px; padding: 10px 12px; border-radius: 8px; background: #fff7ed; border: 1px solid #fed7aa; color: #c2410c; font-size: 13px; line-height: 1.7; }
.tut-important { margin-top: 12px; padding: 14px; border-radius: 10px; background: linear-gradient(135deg, #fff1f2, #ffe4e6); border: 2px solid #fb7185; }
.tut-important strong { display: block; color: #e11d48; font-size: 15px; margin-bottom: 6px; }
.tut-important p { margin: 0; color: #9f1239; font-size: 13px; line-height: 1.8; }

/* 双重认证醒目提示 */
.tut-alert { margin: 12px 0 14px; padding: 14px 16px; border-radius: 12px; background: #fef2f2; border: 2px solid #dc2626; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.45); animation: tut-alert-pulse 1.8s ease-out infinite; }
.tut-alert-title { font-size: 17px; font-weight: 800; color: #dc2626; margin-bottom: 8px; }
.tut-block .tut-alert p { margin: 4px 0; color: #7f1d1d; font-size: 14px; line-height: 1.7; }
.tut-alert strong { color: #b91c1c; }
.tut-alert-steps { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 8px; margin: 10px 0; }
.tut-alert-steps span { padding: 6px 12px; background: #dc2626; color: #fff; font-weight: 700; font-size: 14px; border-radius: 6px; }
.tut-alert-steps b { color: #dc2626; font-size: 18px; }
.tut-block .tut-alert .tut-alert-no { font-weight: 800; color: #dc2626; font-size: 15px; }
.tut-block .tut-alert .tut-alert-sub { font-size: 12px; color: #991b1b; }
@keyframes tut-alert-pulse {
    0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.45); }
    70% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
    100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
}

.mac-chip-btns { display: flex; gap: 12px; }
.mac-chip-btns .download-btn { flex: 1; padding: 12px; background: #667eea; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; }
.mac-chip-btns .download-btn:hover { background: #5568d3; }
.mac-cmd-box { position: relative; margin: 8px 0; }
.mac-cmd-box pre { background: #1e1e1e; color: #4caf50; padding: 12px; padding-right: 90px; border-radius: 6px; overflow-x: auto; font-size: 13px; margin: 0; white-space: pre-wrap; word-break: break-all; }
.mac-copy-btn { position: absolute; top: 8px; right: 8px; background: #4caf50; color: #fff; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px; }

.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: white; border-radius: 12px; padding: 40px; width: 90%; max-width: 400px; position: relative; }
.modal-close { position: absolute; top: 12px; right: 12px; background: none; border: none; font-size: 24px; cursor: pointer; color: #999; }
.modal-icon { width: 60px; height: 60px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 28px; margin: 0 auto 20px; color: white; }
.modal-title { text-align: center; margin-bottom: 24px; font-size: 20px; color: #333; }
.modal-input { width: 100%; padding: 12px; margin-bottom: 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box; }
.modal-input:focus { outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1); }
.modal-primary { width: 100%; padding: 12px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; border-radius: 6px; font-size: 16px; cursor: pointer; margin-top: 12px; transition: transform 0.2s; }
.modal-primary:hover { transform: translateY(-2px); }
.modal-footer { text-align: center; margin-top: 16px; font-size: 14px; color: #666; }
.link-btn { background: none; border: none; color: #667eea; cursor: pointer; font-size: 14px; text-decoration: underline; }
.code-group { display: flex; gap: 8px; margin-bottom: 12px; }
.code-input { flex: 1; margin-bottom: 0; }
.code-btn { flex-shrink: 0; padding: 12px 16px; background: #667eea; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; }
.backup-list { display: flex; flex-direction: column; gap: 12px; max-height: 400px; overflow-y: auto; }
.backup-option { padding: 12px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 6px; cursor: pointer; text-align: left; transition: all 0.2s; }
.backup-option:hover { background: #e8e8ff; border-color: #667eea; }
.file-size { font-size: 12px; color: #999; margin-left: 8px; }
.toast { position: fixed; bottom: 30px; left: 50%; transform: translateX(-50%); background: #333; color: white; padding: 12px 24px; border-radius: 6px; z-index: 2000; }
.my-kefu { position: fixed; right: 20px; bottom: 90px; width: 52px; height: 52px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #fff; border: none; font-size: 12px; font-weight: 600; line-height: 1.3; cursor: pointer; box-shadow: 0 4px 16px rgba(0,0,0,.2); z-index: 999; display: flex; align-items: center; justify-content: center; transition: transform 0.2s; }
.my-kefu:hover { transform: scale(1.05); }
@media (max-width: 768px) { .my-kefu { bottom: 90px; right: 16px; } }
.section { padding: 60px 0; }
.section-header { text-align: center; margin-bottom: 40px; }
.section-header h2 { font-size: 32px; margin: 0 0 12px 0; color: #333; }
.section-header p { color: #999; font-size: 16px; }
.platforms { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 30px; }
.platform { padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px; text-align: center; transition: all 0.3s; }
.platform:hover { border-color: #667eea; box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1); }
.platform-icon { font-size: 40px; display: block; margin-bottom: 12px; }
.platform h3 { margin: 0 0 8px 0; font-size: 18px; color: #333; }
.platform p { margin: 0 0 12px 0; color: #999; font-size: 14px; }
.platform-action { display: flex; flex-direction: column; gap: 8px; }
.download-btn { padding: 10px 16px; background: #667eea; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; transition: background 0.2s; }
.download-btn:hover { background: #5568d3; }
.download-btn.secondary { background: #f0f0f0; color: #333; }
.download-btn.secondary:hover { background: #e0e0e0; }
.plans-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 30px; }
.plan-card { padding: 20px; border: 2px solid #ddd; border-radius: 8px; cursor: pointer; transition: all 0.3s; position: relative; }
.plan-card:hover { border-color: #667eea; }
.plan-card.recommended { border-color: #667eea; background: #f0f2ff; }
.plan-card .badge { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); background: #667eea; color: white; padding: 4px 12px; border-radius: 12px; font-size: 12px; }
.plan-card h3 { margin: 0 0 12px 0; color: #333; }
.plan-card .price { font-size: 28px; color: #667eea; margin-bottom: 4px; }
.plan-card .symbol { font-size: 16px; }
.plan-card .period { color: #999; font-size: 14px; margin-bottom: 12px; }
.plan-card ul { list-style: none; padding: 0; margin: 0; }
.plan-card li { padding: 6px 0; color: #666; font-size: 14px; }

.coupon-section { margin: 30px 0; }
.coupon-input { width: 100%; max-width: 400px; margin: 0 auto; display: block; padding: 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box; }
.coupon-input:focus { outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1); }

.checkout { width: 100%; max-width: 400px; margin: 0 auto; display: block; padding: 14px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; border-radius: 6px; font-size: 16px; cursor: pointer; transition: transform 0.2s; }
.checkout:disabled { opacity: .6; cursor: not-allowed; }
.user-menu { position: relative; display: flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1px solid #e0e0e0; border-radius: 999px; cursor: pointer; font-size: 14px; color: #333; }
.user-menu:hover { border-color: #667eea; }
.user-email { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.caret { font-size: 12px; color: #999; }
.user-dropdown { position: absolute; top: 110%; right: 0; background: #fff; border: 1px solid #eee; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,.1); overflow: hidden; z-index: 100; min-width: 120px; }
.user-dropdown button { display: block; width: 100%; padding: 12px 16px; background: none; border: none; text-align: left; cursor: pointer; font-size: 14px; color: #333; }
.user-dropdown button:hover { background: #f5f5f5; color: #667eea; }
.plan-group { margin-bottom: 30px; }
.plan-group-title { font-size: 18px; font-weight: 700; color: #333; margin-bottom: 16px; }
.device-list { display: flex; flex-direction: column; gap: 12px; }
.device-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border: 1px solid #eee; border-radius: 8px; }
.device-type { font-size: 15px; color: #333; font-weight: 500; }
.device-unbind { padding: 6px 16px; background: #fff; color: #e34; border: 1px solid #e34; border-radius: 6px; cursor: pointer; font-size: 14px; }
.device-unbind:hover { background: #e34; color: #fff; }
.checkout:hover { transform: translateY(-2px); }
.support { margin-bottom: 30px; }
.support h3 { color: #333; margin-bottom: 12px; }
.support-actions { display: flex; gap: 12px; }
.support-actions button { padding: 10px 20px; background: #f0f0f0; border: none; border-radius: 6px; cursor: pointer; transition: background 0.2s; }
.support-actions button:hover { background: #e0e0e0; }
.footer { padding: 40px 0; border-top: 1px solid #eee; text-align: center; color: #999; }
.footer p { margin: 0; }
.invite-content { text-align: center; padding: 40px; background: #f9f9f9; border-radius: 8px; }
.invite-content p { color: #666; line-height: 1.6; }
.invite-section { background: linear-gradient(135deg, #2a1a3e 0%, #1a1626 50%, #0f1420 100%); padding: 80px 0; margin: 40px 0; }
.invite-inner { display: grid; grid-template-columns: 1fr 1.1fr; gap: 48px; align-items: center; }
.invite-badge { display: inline-block; padding: 7px 16px; border-radius: 999px; background: rgba(255,255,255,.1); color: #e5d5ff; font-size: 13px; margin-bottom: 24px; }
.invite-title { font-size: 44px; font-weight: 900; line-height: 1.2; color: #fff; margin: 0 0 20px 0; }
.invite-title em { font-style: normal; background: linear-gradient(90deg, #ff6ec4, #a855f7 50%, #7aa5ff); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.invite-desc { color: #b8b0c8; font-size: 15px; line-height: 1.8; margin-bottom: 32px; max-width: 440px; }
.invite-btn { padding: 15px 32px; background: #fff; color: #1a1626; border: none; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; transition: transform 0.2s; }
.invite-btn:hover { transform: translateY(-2px); }
.invite-btn b { margin-left: 6px; }
.invite-hint { color: #7a7290; font-size: 13px; margin-top: 16px; }
.invite-card { background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: 24px; padding: 32px; backdrop-filter: blur(10px); }
.invite-card-head { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
.invite-card-title { font-size: 18px; font-weight: 700; color: #fff; }
.invite-card-tag { font-size: 12px; color: #9a92b0; padding: 3px 10px; background: rgba(255,255,255,.08); border-radius: 999px; }
.invite-levels { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 28px; }
.level-box { background: rgba(255,255,255,.06); border-radius: 16px; padding: 18px 14px; text-align: left; }
.level-name { font-size: 13px; color: #9a92b0; display: block; margin-bottom: 8px; }
.level-pct { font-size: 30px; font-weight: 900; color: #fff; display: block; margin-bottom: 8px; }
.level-sub { font-size: 12px; color: #9a92b0; }
.invite-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; padding-top: 24px; border-top: 1px solid rgba(255,255,255,.1); }
.stat-label { font-size: 13px; color: #9a92b0; display: block; margin-bottom: 8px; }
.stat-value { font-size: 26px; font-weight: 800; color: #fff; }
.stat-value small { font-size: 14px; font-weight: 400; color: #b8b0c8; }
@media (max-width: 768px) {
    .invite-inner { grid-template-columns: 1fr; gap: 32px; }
    .invite-title { font-size: 34px; }
    .invite-levels { gap: 8px; }
    .level-pct { font-size: 24px; }
}
</style>